import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Printer, Loader2, AlertTriangle,
  XCircle, Settings, Zap, Bot, Factory, Calendar,
  DollarSign, BookOpen, TrendingUp, Building2, Coins,
  CheckCircle2, Users, Target, ArrowRight, PieChart,
  BarChart3, Banknote, TrendingDown, Award,
  Globe, FileText, Cpu, UserCheck, Wrench,
  ChevronLeft, ChevronRight, Presentation, Clock,
  Layers, Lightbulb, ListOrdered, ChevronDown, ChevronUp,
  Download, FileSpreadsheet
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { ReportSecurityNote } from '../components/ReportSecurityNote';
import ProposalGenerator from '../components/ProposalGenerator';
import axios from 'axios';
import { generatePptx } from '../utils/generatePptx';
import { getCurSym, hasPeerValue, metricText, impactText, metricComparison, scorecardStats } from '../utils/scorecard';
import { fetchValueReport } from '../utils/assessmentProgress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to format units - converts 'percentage' to '%' and prefixes a space for readability
const formatUnit = (unit) => {
  if (!unit) return '';
  const lowerUnit = unit.toLowerCase();
  if (lowerUnit === 'percentage' || lowerUnit === 'percent') return '%';
  if (unit === '%' || unit === 'x') return unit;
  return ` ${unit}`;
};

// Collapsible Section for document view
const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = true, iconBg = 'bg-emerald-100', iconColor = 'text-emerald-600' }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border-b border-gray-100">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 sm:p-6 md:p-8 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 sm:w-10 sm:h-10 ${iconBg} rounded-lg flex items-center justify-center`}>
            <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${iconColor}`} />
          </div>
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">{title}</h2>
        </div>
        <div className="sm:hidden">
          {isOpen ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
        </div>
      </button>
      <div className={`${isOpen ? 'block' : 'hidden sm:block'}`}>{children}</div>
    </div>
  );
};

// Slide Component for presentation mode
const Slide = ({ children, className = '', slideNumber, totalSlides }) => (
  <div className={`slide relative w-full bg-white rounded-lg shadow-2xl overflow-hidden ${className}`} style={{ aspectRatio: '16/9' }}>
    {children}
    <div className="absolute bottom-3 right-4 text-xs text-gray-400">{slideNumber} / {totalSlides}</div>
  </div>
);

const SlideHeader = ({ title, subtitle }) => (
  <div className="px-6 sm:px-10 pt-5 sm:pt-6 pb-3">
    <div className="flex items-center gap-3 mb-1">
      <div className="w-1 h-6 sm:h-8 bg-emerald-500 rounded-full"></div>
      <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">{title}</h2>
    </div>
    {subtitle && <p className="text-xs sm:text-sm text-gray-500 ml-4">{subtitle}</p>}
  </div>
);

const ReportPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const reportRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [userName, setUserName] = useState('');
  
  // View mode: 'document' (default) or 'presentation'
  const [viewMode, setViewMode] = useState('document');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [downloadingPptx, setDownloadingPptx] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    if (location.hash !== '#client-proposal' || loading || error || !report || viewMode !== 'document') return;
    const frame = requestAnimationFrame(() => {
      document.getElementById('client-proposal')?.scrollIntoView({ block: 'start', behavior: 'instant' });
    });
    return () => cancelAnimationFrame(frame);
  }, [location.hash, loading, error, report, viewMode]);

  const handleDownloadPdf = async () => {
    try {
      setDownloadingPdf(true);
      const userId = localStorage.getItem('samanvai_user_id');
      const tok = localStorage.getItem(`vsc_token_${userId}`);
      const response = await axios.get(`${API}/analysis/${userId}/report/pdf`, {
        responseType: 'blob',
        headers: tok ? { 'X-Access-Token': tok } : {}
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${companyName.replace(/\s+/g, '_')}_Value_Scorecard.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download PDF:', err);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleDownloadPptx = async () => {
    try {
      setDownloadingPptx(true);
      await generatePptx(report, analysis, companyName, userName);
    } catch (err) {
      console.error('Failed to generate PowerPoint:', err);
      alert('Failed to generate PowerPoint. Please try again.');
    } finally {
      setDownloadingPptx(false);
    }
  };

  useEffect(() => {
    const queryId = new URLSearchParams(window.location.search).get('id');
    const storedUserId = localStorage.getItem('samanvai_user_id');
    const uid = queryId || storedUserId;
    const storedUserName = localStorage.getItem('samanvai_user_name');
    const storedCompany = localStorage.getItem('samanvai_company');
    
    if (!uid) {
      navigate('/dashboard');
      return;
    }
    if (queryId) localStorage.setItem('samanvai_user_id', queryId);
    
    setUserName(storedUserName || 'User');
    setCompanyName(storedCompany || 'Your Company');
    fetchData(uid);
  }, [navigate]);

  const fetchData = async (uid, retry = false) => {
    try {
      setLoading(true);
      setError('');
      const tok = localStorage.getItem(`vsc_token_${uid}`);
      const headers = tok ? { 'X-Access-Token': tok } : {};
      const [reportResponse, analysisResponse] = await Promise.all([
        fetchValueReport(`${API}/analysis/${uid}/report`, headers, retry),
        axios.get(`${API}/analysis/${uid}`, { headers })
      ]);
      setReport(reportResponse.data);
      setAnalysis(analysisResponse.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      if (err.response?.status === 403) {
        navigate(`/dashboard?id=${uid}`);
        return;
      }
      setError(err.response?.data?.detail || 'Failed to generate report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = () => new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const handlePrint = () => window.print();

  const isLowerBetter = (kpiName) => {
    const name = kpiName.toLowerCase();
    return name.includes('cost') || name.includes('churn') || name.includes('turnover') ||
           name.includes('incident') || name.includes('downtime') || name.includes('defect') ||
           name.includes('error') || name.includes('emission') || name.includes('cac') ||
           name.includes('attrition') || name.includes('debt') || name.includes('lead time');
  };

  // Extract data
  const kpiData = analysis?.kpis || [];
  const peerComparison = (analysis?.peer_comparison || []).filter((pc) => [pc.peer1Value, pc.peer2Value, pc.peer3Value].some(hasPeerValue));
  const curSym = getCurSym(analysis);
  const peers = analysis?.peers || [];
  const fiveYearRoadmap = analysis?.five_year_roadmap || [];
  const areasOfImprovement = analysis?.areas_of_improvement || [];

  const { kpisAboveAvg, kpisBelowAvg, totalPotentialImpact } = scorecardStats(kpiData, isLowerBetter);
  const reportCompanyName = report?.company_name || companyName;

  // Build slides for presentation mode
  const buildSlides = () => {
    const slidesList = [];
    slidesList.push({ type: 'cover', title: 'Cover' });
    slidesList.push({ type: 'agenda', title: 'Agenda' });
    slidesList.push({ type: 'executive-summary', title: 'Executive Summary' });
    slidesList.push({ type: 'performance-overview', title: 'Performance Overview' });
    if (kpiData.length > 0) slidesList.push({ type: 'kpi-analysis', title: 'KPI Analysis' });
    if (report?.kpi_improvement_values?.length > 0) slidesList.push({ type: 'kpi-values', title: 'KPI Improvement Values' });
    if (peers.length > 0 && peerComparison.length > 0) slidesList.push({ type: 'peer-comparison', title: 'Peer Comparison' });
    if (report?.financial_impact_summary) slidesList.push({ type: 'financial-impact', title: 'Financial Impact' });
    if (report?.roi_analysis) slidesList.push({ type: 'roi-analysis', title: 'ROI Analysis' });
    if (report?.business_value_realization) slidesList.push({ type: 'value-realization', title: 'Business Value Realization' });
    if (fiveYearRoadmap.length > 0) slidesList.push({ type: 'roadmap', title: '5-Year Strategic Roadmap' });
    if (report?.strategy_roadmap?.length > 0) slidesList.push({ type: 'strategy-roadmap', title: 'Strategy Roadmap' });
    slidesList.push({ type: 'eoai', title: 'Value Creation Framework' });
    if (report?.total_investment || report?.investment_breakdown?.length > 0) slidesList.push({ type: 'investment', title: 'Investment & Resources' });
    if (report?.implementation_plan?.length > 0) slidesList.push({ type: 'implementation', title: 'Implementation Plan' });
    if (areasOfImprovement.length > 0) slidesList.push({ type: 'recommendations', title: 'Key Recommendations' });
    if (report?.case_studies?.length > 0) slidesList.push({ type: 'case-studies', title: 'Industry Case Studies' });
    slidesList.push({ type: 'summary', title: 'Summary & Next Steps' });
    slidesList.push({ type: 'cybersecurity', title: 'Cybersecurity & Data Protection' });
    return slidesList;
  };

  const slides = buildSlides();
  const totalSlides = slides.length;

  const nextSlide = () => setCurrentSlide(prev => Math.min(prev + 1, totalSlides - 1));
  const prevSlide = () => setCurrentSlide(prev => Math.max(prev - 1, 0));
  const goToSlide = (index) => { if (index >= 0 && index < totalSlides) setCurrentSlide(index); };

  // Keyboard navigation for presentation mode
  useEffect(() => {
    if (viewMode !== 'presentation') return;
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); nextSlide(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); prevSlide(); }
      else if (e.key === 'Escape') { setViewMode('document'); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [viewMode, totalSlides]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 sm:w-16 sm:h-16 text-emerald-600 animate-spin mx-auto mb-4" />
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Generating Your Value Scorecard</h2>
          <p className="text-sm sm:text-base text-gray-600">Our AI is analyzing your data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-12 h-12 sm:w-16 sm:h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Report Generation Failed</h2>
          <p className="text-sm sm:text-base text-gray-600 mb-6">{error}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button variant="outline" onClick={() => navigate('/dashboard')}><ArrowLeft className="w-4 h-4 mr-2" />Back to Dashboard</Button>
            <Button data-testid="retry-value-scorecard" onClick={() => fetchData(localStorage.getItem('samanvai_user_id'), true)}>Try Again</Button>
          </div>
        </div>
      </div>
    );
  }

  // ============ PRESENTATION MODE ============
  if (viewMode === 'presentation') {
    const renderSlide = () => {
      if (currentSlide >= slides.length) return null;
      const slide = slides[currentSlide];
      
      switch (slide.type) {
        case 'cover':
          return (
            <Slide slideNumber={currentSlide + 1} totalSlides={totalSlides}>
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-800">
                <div className="absolute top-0 right-0 w-1/3 h-full bg-white/5"></div>
              </div>
              <div className="relative h-full flex flex-col justify-between p-6 sm:p-10 text-white">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center"><span className="text-white font-bold text-xl">S</span></div>
                  <span className="text-xl font-bold">SAMANVAI</span>
                </div>
                <div className="flex-1 flex flex-col justify-center">
                  <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3">{reportCompanyName}</h1>
                  <h2 className="text-2xl sm:text-3xl font-light text-emerald-200 mb-4">Value Scorecard</h2>
                  <p className="text-sm sm:text-base text-white/80 max-w-xl">Comprehensive Performance Analysis & Strategic Roadmap</p>
                </div>
                <div className="flex flex-wrap gap-4 sm:gap-8 text-sm border-t border-white/20 pt-4">
                  <div><p className="text-white/50 text-xs uppercase mb-1">Industry</p><p className="font-medium">{report?.sector || analysis?.sector || 'N/A'}</p></div>
                  <div><p className="text-white/50 text-xs uppercase mb-1">Prepared For</p><p className="font-medium">{userName}</p></div>
                  <div><p className="text-white/50 text-xs uppercase mb-1">Date</p><p className="font-medium">{formatDate()}</p></div>
                  {analysis?.data_governance?.reporting_period_used && (
                    <div><p className="text-white/50 text-xs uppercase mb-1">Reporting Period</p><p className="font-medium" data-testid="report-cover-reporting-period">{analysis.data_governance.reporting_period_used}</p></div>
                  )}
                  {analysis?.data_governance?.period_requested && (
                    <div><p className="text-white/50 text-xs uppercase mb-1">Period Requested</p><p className="font-medium" data-testid="report-cover-period-requested">{analysis.data_governance.period_requested}</p></div>
                  )}
                  {analysis?.data_governance?.report_generated_on && (
                    <div><p className="text-white/50 text-xs uppercase mb-1">Generated On</p><p className="font-medium" data-testid="report-cover-generated-on">{analysis.data_governance.report_generated_on}</p></div>
                  )}
                </div>
              </div>
            </Slide>
          );

        case 'agenda':
          return (
            <Slide slideNumber={currentSlide + 1} totalSlides={totalSlides}>
              <SlideHeader title="Agenda" subtitle="What we will cover today" />
              <div className="px-6 sm:px-10 py-4 grid grid-cols-2 gap-3">
                {[
                  { icon: BookOpen, title: 'Executive Summary', desc: 'Key findings and recommendations' },
                  { icon: BarChart3, title: 'Performance Overview', desc: 'Current state analysis' },
                  { icon: Target, title: 'KPI Analysis', desc: 'Benchmarking against industry' },
                  { icon: Building2, title: 'Peer Comparison', desc: 'Competitive positioning' },
                  { icon: Banknote, title: 'Financial Impact', desc: 'Value creation potential' },
                  { icon: Calendar, title: 'Strategic Roadmap', desc: '5-year transformation plan' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{item.title}</p>
                      <p className="text-xs text-gray-500">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Slide>
          );

        case 'executive-summary':
          return (
            <Slide slideNumber={currentSlide + 1} totalSlides={totalSlides}>
              <SlideHeader title="Executive Summary" />
              <div className="px-6 sm:px-10 py-2 h-[calc(100%-90px)] overflow-auto">
                <p className="text-sm text-gray-700 leading-relaxed mb-4">{(report?.executive_summary || analysis?.executive_summary || '').substring(0, 600)}...</p>
                {report?.projected_improvements && (
                  <div className="grid grid-cols-5 gap-2 mt-4">
                    {Object.entries(report.projected_improvements).filter(([, value]) => String(value).length <= 40).slice(0, 5).map(([key, value]) => (
                      <div key={key} className="bg-emerald-50 rounded-lg p-3 text-center">
                        <p className="text-xl font-bold text-emerald-600">{value}</p>
                        <p className="text-xs text-gray-600 capitalize">{key.replace(/_/g, ' ')}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Slide>
          );

        case 'performance-overview':
          return (
            <Slide slideNumber={currentSlide + 1} totalSlides={totalSlides}>
              <SlideHeader title="Performance Overview" />
              <div className="px-6 sm:px-10 py-4">
                <div className="grid grid-cols-4 gap-4 mb-4">
                  <div className="bg-emerald-50 rounded-xl p-4 text-center">
                    <TrendingUp className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                    <p data-testid="slide-summary-above-average" className="text-2xl font-bold text-emerald-600">{kpisAboveAvg ?? 'N/A'}</p>
                    <p className="text-xs text-gray-600">KPIs Above Average</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-4 text-center">
                    <AlertTriangle className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                    <p data-testid="slide-summary-below-average" className="text-2xl font-bold text-amber-600">{kpisBelowAvg ?? 'N/A'}</p>
                    <p className="text-xs text-gray-600">Need Attention</p>
                  </div>
                  <div className="bg-purple-50 rounded-xl p-4 text-center">
                    <Coins className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                    <p data-testid="slide-summary-impact" className="text-2xl font-bold text-purple-600">{impactText(totalPotentialImpact, curSym, 0)}</p>
                    <p className="text-xs text-gray-600">1% Improvement Value</p>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-4 text-center">
                    <Target className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-blue-600">{kpiData.length}</p>
                    <p className="text-xs text-gray-600">KPIs Analyzed</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600">{reportCompanyName} has {kpisAboveAvg} KPIs performing above industry average and {kpisBelowAvg} KPIs that need attention.</p>
              </div>
            </Slide>
          );

        case 'kpi-analysis':
          return (
            <Slide slideNumber={currentSlide + 1} totalSlides={totalSlides}>
              <SlideHeader title="KPI Analysis" subtitle="Benchmarking against industry standards" />
              <div className="px-6 sm:px-10 py-2 h-[calc(100%-90px)] overflow-auto">
                <div className="flex items-center gap-4 text-xs mb-2">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-500 rounded-full"></span>Underperforming</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded-full"></span>Above Average</span>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-2 text-left text-xs font-semibold text-gray-500">KPI</th>
                      <th className="px-2 py-2 text-center text-xs font-semibold text-gray-500">Your Value</th>
                      <th className="px-2 py-2 text-center text-xs font-semibold text-gray-500">Industry Avg</th>
                      <th className="px-2 py-2 text-center text-xs font-semibold text-gray-500">Top Performer</th>
                      <th className="px-2 py-2 text-right text-xs font-semibold text-gray-500">1% Impact</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {kpiData.slice(0, 6).map((kpi, idx) => {
                      const lowerIsBetter = isLowerBetter(kpi.name);
                      const comparison = metricComparison(kpi, lowerIsBetter);
                      const isAboveAvg = comparison === null || comparison >= 0;
                      return (
                        <tr key={idx} className={!isAboveAvg ? 'bg-red-50/50' : ''}>
                          <td className="px-2 py-2 text-xs">{kpi.name}</td>
                          <td data-testid={`slide-kpi-value-${idx}`} className={`px-2 py-2 text-center text-xs font-semibold ${comparison === null ? 'text-gray-600' : !isAboveAvg ? 'text-red-600' : 'text-emerald-600'}`}>{metricText(kpi.yourValue, formatUnit(kpi.unit))}</td>
                          <td data-testid={`slide-kpi-industry-${idx}`} className="px-2 py-2 text-center text-xs text-gray-500">{metricText(kpi.industryAvg, formatUnit(kpi.unit))}</td>
                          <td data-testid={`slide-kpi-top-${idx}`} className="px-2 py-2 text-center text-xs text-amber-600">{metricText(kpi.topPerformer, formatUnit(kpi.unit))}</td>
                          <td data-testid={`slide-kpi-impact-${idx}`} className="px-2 py-2 text-right text-xs">{impactText(kpi.impactPer1Percent, curSym)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Slide>
          );

        case 'kpi-values':
          return (
            <Slide slideNumber={currentSlide + 1} totalSlides={totalSlides}>
              <SlideHeader title="KPI Improvement Values" subtitle="Potential value from targeted improvements" />
              <div className="px-6 sm:px-10 py-2 h-[calc(100%-90px)] overflow-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-2 text-left text-xs font-semibold text-gray-500">KPI</th>
                      <th className="px-2 py-2 text-center text-xs font-semibold text-gray-500">Current</th>
                      <th className="px-2 py-2 text-center text-xs font-semibold text-gray-500">Target</th>
                      <th className="px-2 py-2 text-right text-xs font-semibold text-gray-500">Annual Impact</th>
                      <th className="px-2 py-2 text-right text-xs font-semibold text-emerald-600">3-Year Value</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(report?.kpi_improvement_values || []).slice(0, 6).map((kpi, idx) => (
                      <tr key={idx}>
                        <td className="px-2 py-2 text-xs font-medium">{kpi.kpi_name}</td>
                        <td className="px-2 py-2 text-center text-xs text-gray-600">{kpi.current_value}</td>
                        <td className="px-2 py-2 text-center text-xs text-blue-600">{kpi.target_value}</td>
                        <td className="px-2 py-2 text-right text-xs">{kpi.annual_value_impact}</td>
                        <td className="px-2 py-2 text-right text-xs font-bold text-emerald-600">{kpi.three_year_value_impact}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Slide>
          );

        case 'peer-comparison':
          return (
            <Slide slideNumber={currentSlide + 1} totalSlides={totalSlides}>
              <SlideHeader title="Peer Comparison" subtitle={`Comparing ${reportCompanyName} with: ${peers.join(', ')}`} />
              <div className="px-6 sm:px-10 py-2 h-[calc(100%-90px)] overflow-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-2 text-left text-xs font-semibold text-gray-500">KPI</th>
                      <th className="px-2 py-2 text-center text-xs font-semibold text-emerald-600 bg-emerald-50">{reportCompanyName.slice(0, 12)}</th>
                      {peers[0] && <th className="px-2 py-2 text-center text-xs font-semibold text-blue-600">{peers[0].slice(0, 12)}</th>}
                      {peers[1] && <th className="px-2 py-2 text-center text-xs font-semibold text-purple-600">{peers[1].slice(0, 12)}</th>}
                      {peers[2] && <th className="px-2 py-2 text-center text-xs font-semibold text-orange-600">{peers[2].slice(0, 12)}</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {peerComparison.slice(0, 6).map((pc, idx) => (
                      <tr key={idx}>
                        <td className="px-2 py-2 text-xs font-medium">{pc.name}</td>
                        <td data-testid={`slide-peer-company-${idx}`} className="px-2 py-2 text-center text-xs font-semibold text-emerald-600 bg-emerald-50">{metricText(pc.yourValue, formatUnit(pc.unit))}</td>
                        {peers[0] && <td className="px-2 py-2 text-center text-xs text-gray-600">{pc.peer1Value ?? '-'}{pc.peer1Value != null ? formatUnit(pc.unit) : ''}</td>}
                        {peers[1] && <td className="px-2 py-2 text-center text-xs text-gray-600">{pc.peer2Value ?? '-'}{pc.peer2Value != null ? formatUnit(pc.unit) : ''}</td>}
                        {peers[2] && <td className="px-2 py-2 text-center text-xs text-gray-600">{pc.peer3Value ?? '-'}{pc.peer3Value != null ? formatUnit(pc.unit) : ''}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Slide>
          );

        case 'financial-impact':
          return (
            <Slide slideNumber={currentSlide + 1} totalSlides={totalSlides}>
              <SlideHeader title="Financial Impact Summary" />
              <div className="px-6 sm:px-10 py-4">
                <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl p-4 mb-4 text-center">
                  <p className="text-xs opacity-80 mb-1">TOTAL BUSINESS VALUE POTENTIAL</p>
                  <p className="text-3xl font-bold">{report?.financial_impact_summary?.total_business_value || 'N/A'}</p>
                  <p className="text-xs opacity-70">Over 3-Year Implementation Period</p>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-500 mb-1">Cost Savings</p>
                    <p className="text-lg font-bold text-green-600">{report?.financial_impact_summary?.total_potential_savings || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-500 mb-1">Revenue Uplift</p>
                    <p className="text-lg font-bold text-blue-600">{report?.financial_impact_summary?.revenue_uplift_potential || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-500 mb-1">Efficiency Gains</p>
                    <p className="text-lg font-bold text-amber-600">{report?.financial_impact_summary?.efficiency_gains_value || 'N/A'}</p>
                  </div>
                  {report?.financial_impact_summary?.cost_reduction_potential && (
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-gray-500 mb-1">Cost Reduction</p>
                      <p className="text-lg font-bold text-purple-600">{report.financial_impact_summary.cost_reduction_potential}</p>
                    </div>
                  )}
                </div>
                {report?.financial_impact_summary?.mece_note && (
                  <p className="text-xs text-gray-500 mt-3 text-center" data-testid="fis-slide-mece-note">{report.financial_impact_summary.mece_note}</p>
                )}
              </div>
            </Slide>
          );

        case 'roi-analysis':
          return (
            <Slide slideNumber={currentSlide + 1} totalSlides={totalSlides}>
              <SlideHeader title="ROI Analysis" />
              <div className="px-6 sm:px-10 py-4">
                <div className="grid grid-cols-4 gap-3 mb-4">
                  <div className="bg-green-50 rounded-xl p-4 text-center">
                    <p className="text-xs text-gray-500 mb-1">Year 1 ROI</p>
                    <p className="text-2xl font-bold text-green-600">{report?.roi_analysis?.year_1_roi || 'N/A'}</p>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-4 text-center">
                    <p className="text-xs text-gray-500 mb-1">Year 3 ROI</p>
                    <p className="text-2xl font-bold text-blue-600">{report?.roi_analysis?.year_3_roi || 'N/A'}</p>
                  </div>
                  <div className="bg-purple-50 rounded-xl p-4 text-center">
                    <p className="text-xs text-gray-500 mb-1">Payback Period</p>
                    <p className="text-2xl font-bold text-purple-600">{report?.roi_analysis?.payback_period || 'N/A'}</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-4 text-center">
                    <p className="text-xs text-gray-500 mb-1">Net Present Value</p>
                    <p className="text-2xl font-bold text-amber-600">{report?.roi_analysis?.net_present_value || 'N/A'}</p>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-sm font-semibold text-center mb-3">Annual Financial Projections</p>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div><p className="text-xs text-gray-500">Year 1</p><p className="text-xl font-bold">{report?.roi_analysis?.year_1_value || 'N/A'}</p></div>
                    <div><p className="text-xs text-gray-500">Year 2</p><p className="text-xl font-bold">{report?.roi_analysis?.year_2_value || 'N/A'}</p></div>
                    <div><p className="text-xs text-gray-500">Year 3</p><p className="text-xl font-bold">{report?.roi_analysis?.year_3_value || 'N/A'}</p></div>
                  </div>
                </div>
              </div>
            </Slide>
          );

        case 'value-realization':
          return (
            <Slide slideNumber={currentSlide + 1} totalSlides={totalSlides}>
              <SlideHeader title="Business Value Realization Timeline" />
              <div className="px-6 sm:px-10 py-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
                    <div className="flex items-center gap-2 mb-2"><Zap className="w-5 h-5 text-yellow-500" /><span className="font-bold text-gray-900">Quick Wins</span></div>
                    <p className="text-xs text-gray-500 mb-2">0-6 months</p>
                    <p className="text-xs text-gray-600 mb-2">{report?.business_value_realization?.quick_wins_description?.substring(0, 100) || 'N/A'}</p>
                    <p className="text-lg font-bold text-yellow-600">{report?.business_value_realization?.quick_wins_value || 'N/A'}</p>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                    <div className="flex items-center gap-2 mb-2"><TrendingUp className="w-5 h-5 text-blue-500" /><span className="font-bold text-gray-900">Medium Term</span></div>
                    <p className="text-xs text-gray-500 mb-2">6-18 months</p>
                    <p className="text-xs text-gray-600 mb-2">{report?.business_value_realization?.medium_term_description?.substring(0, 100) || 'N/A'}</p>
                    <p className="text-lg font-bold text-blue-600">{report?.business_value_realization?.medium_term_value || 'N/A'}</p>
                  </div>
                  <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                    <div className="flex items-center gap-2 mb-2"><Target className="w-5 h-5 text-green-500" /><span className="font-bold text-gray-900">Long Term</span></div>
                    <p className="text-xs text-gray-500 mb-2">18-36 months</p>
                    <p className="text-xs text-gray-600 mb-2">{report?.business_value_realization?.long_term_description?.substring(0, 100) || 'N/A'}</p>
                    <p className="text-lg font-bold text-green-600">{report?.business_value_realization?.long_term_value || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </Slide>
          );

        case 'roadmap':
          return (
            <Slide slideNumber={currentSlide + 1} totalSlides={totalSlides}>
              <SlideHeader title="5-Year Strategic Roadmap" />
              <div className="px-6 sm:px-10 py-2 h-[calc(100%-90px)] overflow-auto">
                <div className="space-y-2">
                  {fiveYearRoadmap.slice(0, 5).map((year, idx) => (
                    <div key={idx} className="flex items-start gap-3 bg-gray-50 rounded-lg p-3">
                      <div className="w-16 h-8 bg-purple-600 rounded flex items-center justify-center text-white font-bold text-xs flex-shrink-0">Year {idx + 1}</div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 text-sm">{year.focus}</p>
                        <p className="text-xs text-gray-500">{year.goals?.slice(0, 2).join('; ')}</p>
                      </div>
                      {year.estimated_investment && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">{year.estimated_investment}</span>}
                    </div>
                  ))}
                </div>
              </div>
            </Slide>
          );

        case 'strategy-roadmap':
          return (
            <Slide slideNumber={currentSlide + 1} totalSlides={totalSlides}>
              <SlideHeader title="Strategy Roadmap" />
              <div className="px-6 sm:px-10 py-2 h-[calc(100%-90px)] overflow-auto">
                <div className="space-y-2">
                  {(report?.strategy_roadmap || []).slice(0, 4).map((item, idx) => (
                    <div key={idx} className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-semibold text-gray-900 text-sm">{item.phase || item.title}</p>
                        <span className="text-xs text-gray-500">{item.timeline}</span>
                      </div>
                      <p className="text-xs text-gray-600">{item.description?.substring(0, 150)}</p>
                    </div>
                  ))}
                </div>
              </div>
            </Slide>
          );

        case 'eoai':
          return (
            <Slide slideNumber={currentSlide + 1} totalSlides={totalSlides}>
              <SlideHeader title="Value Creation Framework" subtitle="Eliminate • Optimize • Automate • Industrialize (EOAI)" />
              <div className="px-6 sm:px-10 py-2">
                <div className="grid grid-cols-4 gap-3">
                  <div className="bg-red-50 rounded-lg p-3 border-l-4 border-red-500">
                    <p className="font-bold text-red-600 text-sm mb-2">Eliminate</p>
                    {(report?.eliminate || []).slice(0, 2).map((item, idx) => (
                      <p key={idx} className="text-xs text-gray-600 mb-1">• {item.title}</p>
                    ))}
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3 border-l-4 border-amber-500">
                    <p className="font-bold text-amber-600 text-sm mb-2">Optimize</p>
                    {(report?.optimize || []).slice(0, 2).map((item, idx) => (
                      <p key={idx} className="text-xs text-gray-600 mb-1">• {item.title}</p>
                    ))}
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3 border-l-4 border-blue-500">
                    <p className="font-bold text-blue-600 text-sm mb-2">Automate</p>
                    {(report?.automate || []).slice(0, 2).map((item, idx) => (
                      <p key={idx} className="text-xs text-gray-600 mb-1">• {item.title}</p>
                    ))}
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 border-l-4 border-purple-500">
                    <p className="font-bold text-purple-600 text-sm mb-2">Industrialize</p>
                    {(report?.industrialize || []).slice(0, 2).map((item, idx) => (
                      <p key={idx} className="text-xs text-gray-600 mb-1">• {item.title}</p>
                    ))}
                  </div>
                </div>
              </div>
            </Slide>
          );

        case 'investment':
          return (
            <Slide slideNumber={currentSlide + 1} totalSlides={totalSlides}>
              <SlideHeader title="Investment & Resources" />
              <div className="px-6 sm:px-10 py-4">
                {report?.total_investment && (
                  <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl p-4 mb-4 text-center">
                    <p className="text-xs opacity-80 mb-1">TOTAL INVESTMENT REQUIRED</p>
                    <p className="text-3xl font-bold">{report.total_investment}</p>
                  </div>
                )}
                {report?.investment_breakdown?.length > 0 && (
                  <div className="grid grid-cols-3 gap-3">
                    {report.investment_breakdown.slice(0, 6).map((item, idx) => (
                      <div key={idx} className="bg-gray-50 rounded-lg p-3">
                        <p className="font-semibold text-gray-900 text-sm">{item.area}</p>
                        <p className="text-lg font-bold text-indigo-600">{item.amount}</p>
                        <p className="text-xs text-gray-500">{item.percentage}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Slide>
          );

        case 'implementation':
          return (
            <Slide slideNumber={currentSlide + 1} totalSlides={totalSlides}>
              <SlideHeader title="Implementation Plan" />
              <div className="px-6 sm:px-10 py-2 h-[calc(100%-90px)] overflow-auto">
                <div className="space-y-2">
                  {(report?.implementation_plan || []).slice(0, 5).map((step, idx) => (
                    <div key={idx} className="flex items-start gap-3 bg-gray-50 rounded-lg p-3">
                      <div className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">{idx + 1}</div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="font-semibold text-gray-900 text-sm">{step.step}</p>
                          <span className="text-xs text-orange-600 bg-orange-100 px-2 py-0.5 rounded">{step.timeline}</span>
                        </div>
                        <p className="text-xs text-gray-600">{step.description?.substring(0, 100)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Slide>
          );

        case 'recommendations':
          return (
            <Slide slideNumber={currentSlide + 1} totalSlides={totalSlides}>
              <SlideHeader title="Key Recommendations" />
              <div className="px-6 sm:px-10 py-2 h-[calc(100%-90px)] overflow-auto">
                <div className="grid grid-cols-3 gap-3">
                  {areasOfImprovement.slice(0, 6).map((area, idx) => (
                    <div key={idx} className={`rounded-lg p-3 ${area.priority === 'High' ? 'bg-red-50 border-l-4 border-red-500' : area.priority === 'Medium' ? 'bg-amber-50 border-l-4 border-amber-500' : 'bg-gray-50 border-l-4 border-gray-400'}`}>
                      <div className="flex items-start justify-between mb-1">
                        <p className="font-semibold text-gray-900 text-sm">{area.area}</p>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${area.priority === 'High' ? 'bg-red-100 text-red-700' : area.priority === 'Medium' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'}`}>{area.priority}</span>
                      </div>
                      <p className="text-xs text-emerald-600">{area.potentialImpact}</p>
                    </div>
                  ))}
                </div>
              </div>
            </Slide>
          );

        case 'case-studies':
          return (
            <Slide slideNumber={currentSlide + 1} totalSlides={totalSlides}>
              <SlideHeader title="Industry Case Studies" />
              <div className="px-6 sm:px-10 py-2 h-[calc(100%-90px)] overflow-auto">
                <div className="space-y-3">
                  {(report?.case_studies || []).slice(0, 2).map((cs, idx) => (
                    <div key={idx} className="bg-orange-50 rounded-xl p-4 border border-orange-100">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-orange-600 rounded-lg flex items-center justify-center text-white font-bold">{cs.company?.charAt(0) || 'C'}</div>
                        <div>
                          <p className="font-bold text-gray-900">{cs.company}</p>
                          <p className="text-xs text-orange-600">{cs.industry}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div><span className="font-semibold text-red-600">Challenge:</span> <span className="text-gray-600">{typeof cs.challenge === 'string' ? cs.challenge.substring(0, 80) : 'N/A'}</span></div>
                        <div><span className="font-semibold text-blue-600">Solution:</span> <span className="text-gray-600">{typeof cs.solution === 'string' ? cs.solution.substring(0, 80) : 'N/A'}</span></div>
                        <div><span className="font-semibold text-emerald-600">Results:</span> <span className="text-gray-700 font-medium">{typeof cs.results === 'string' ? cs.results.substring(0, 80) : Array.isArray(cs.results) ? cs.results.join(', ').substring(0, 80) : 'N/A'}</span></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Slide>
          );

        case 'summary':
          return (
            <Slide slideNumber={currentSlide + 1} totalSlides={totalSlides}>
              <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-emerald-900"></div>
              <div className="relative h-full flex flex-col p-6 sm:p-10 text-white">
                <h2 className="text-xl sm:text-2xl font-bold mb-4">Summary & Next Steps</h2>
                <div className="flex-1 grid sm:grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-semibold text-emerald-400 mb-3">Key Takeaways</h3>
                    <ul className="space-y-2 text-xs text-gray-300">
                      <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5" /><span><strong className="text-white">{kpiData.length} KPIs analyzed</strong> - {kpisBelowAvg} need attention</span></li>
                      <li data-testid="slide-impact-conclusion" className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5" /><span><strong className="text-white">{impactText(totalPotentialImpact, curSym, 0)}</strong> quantified 1% improvement potential</span></li>
                      {report?.financial_impact_summary?.total_business_value && <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5" /><span><strong className="text-white">{report.financial_impact_summary.total_business_value}</strong> total value over 3 years</span></li>}
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-emerald-400 mb-3">Next Steps</h3>
                    <ol className="space-y-2 text-xs text-gray-300">
                      <li className="flex items-start gap-2"><span className="w-5 h-5 bg-emerald-600 rounded-full flex items-center justify-center font-bold">1</span>Schedule executive workshop</li>
                      <li className="flex items-start gap-2"><span className="w-5 h-5 bg-emerald-600 rounded-full flex items-center justify-center font-bold">2</span>Prioritize quick wins</li>
                      <li className="flex items-start gap-2"><span className="w-5 h-5 bg-emerald-600 rounded-full flex items-center justify-center font-bold">3</span>Develop implementation roadmap</li>
                    </ol>
                  </div>
                </div>
                <div className="pt-4 border-t border-white/20 flex items-center justify-between">
                  <div className="flex items-center gap-2"><div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center"><span className="text-white font-bold">S</span></div><span className="font-bold">SAMANVAI</span></div>
                  <span className="text-xs text-gray-400">contact@samanvai.com</span>
                </div>
              </div>
            </Slide>
          );

        case 'cybersecurity':
          return (
            <Slide slideNumber={currentSlide + 1} totalSlides={totalSlides}>
              <div className="h-full overflow-auto px-6 sm:px-10 py-4">
                <ReportSecurityNote />
              </div>
            </Slide>
          );

        default:
          return (
            <Slide slideNumber={currentSlide + 1} totalSlides={totalSlides}>
              <SlideHeader title={slide.title} />
              <div className="px-6 sm:px-10 py-2 h-[calc(100%-80px)] overflow-auto">
                <p className="text-gray-500 text-sm">Content for {slide.title}</p>
              </div>
            </Slide>
          );
      }
    };

    return (
      <div className="min-h-screen bg-gray-900" ref={reportRef}>
        <header className="fixed top-0 left-0 right-0 h-12 bg-gray-800/90 backdrop-blur border-b border-gray-700 z-50 flex items-center justify-between px-4">
          <Button variant="ghost" size="sm" onClick={() => setViewMode('document')} className="text-gray-300 hover:text-white hover:bg-gray-700 h-8">
            <ArrowLeft className="w-4 h-4 mr-1" />Exit Presentation
          </Button>
          <span className="text-gray-400 text-sm">{reportCompanyName} Value Scorecard</span>
          <Button variant="ghost" size="sm" onClick={handlePrint} className="text-gray-300 hover:text-white hover:bg-gray-700 h-8"><Printer className="w-4 h-4" /></Button>
        </header>
        <main className="pt-16 pb-20 px-4 sm:px-8 md:px-16">
          <div className="max-w-5xl mx-auto">{renderSlide()}</div>
        </main>
        <nav className="fixed bottom-0 left-0 right-0 h-14 bg-gray-800/90 backdrop-blur border-t border-gray-700 flex items-center justify-between px-4 z-50">
          <div className="hidden sm:flex items-center gap-1">
            {slides.map((_, idx) => (
              <button key={idx} onClick={() => goToSlide(idx)} className={`w-2 h-2 rounded-full transition-all ${idx === currentSlide ? 'bg-emerald-500 w-5' : 'bg-gray-600 hover:bg-gray-500'}`} />
            ))}
          </div>
          <div className="sm:hidden text-gray-400 text-sm">{currentSlide + 1} / {totalSlides}</div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={prevSlide} disabled={currentSlide === 0} className="text-gray-300 hover:text-white disabled:opacity-30 h-9"><ChevronLeft className="w-5 h-5" /></Button>
            <Button variant="ghost" size="sm" onClick={nextSlide} disabled={currentSlide === totalSlides - 1} className="text-gray-300 hover:text-white disabled:opacity-30 h-9"><ChevronRight className="w-5 h-5" /></Button>
          </div>
          <div className="hidden lg:flex items-center gap-2 text-xs text-gray-500"><span className="px-1.5 py-0.5 bg-gray-700 rounded">←→</span>Navigate<span className="px-1.5 py-0.5 bg-gray-700 rounded ml-2">Esc</span>Exit</div>
        </nav>
      </div>
    );
  }

  // ============ DOCUMENT MODE (DEFAULT) ============
  return (
    <div className="min-h-screen bg-gray-100 print:bg-white" data-testid="report-page" ref={reportRef}>
      <style>{`@media print { .no-print { display: none !important; } body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }`}</style>

      {/* Navigation Bar */}
      <header className="no-print fixed top-0 left-0 right-0 h-14 sm:h-16 bg-white border-b border-gray-200 z-50 flex items-center justify-between px-3 sm:px-4 md:px-6">
        <div className="flex items-center gap-2 sm:gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="text-xs sm:text-sm px-2 sm:px-3" data-testid="btn-back-to-dashboard">
            <ArrowLeft className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Back to Dashboard</span>
          </Button>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleDownloadPdf} 
            disabled={downloadingPdf}
            className="text-xs sm:text-sm px-2 sm:px-3 border-red-200 text-red-700 hover:bg-red-50" 
            data-testid="btn-download-pdf"
          >
            {downloadingPdf ? (
              <Loader2 className="w-4 h-4 animate-spin sm:mr-2" />
            ) : (
              <FileText className="w-4 h-4 sm:mr-2" />
            )}
            <span className="hidden sm:inline">{downloadingPdf ? 'Generating...' : 'Download PDF'}</span>
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleDownloadPptx} 
            disabled={downloadingPptx}
            className="text-xs sm:text-sm px-2 sm:px-3 border-blue-200 text-blue-700 hover:bg-blue-50" 
            data-testid="btn-download-pptx"
          >
            {downloadingPptx ? (
              <Loader2 className="w-4 h-4 animate-spin sm:mr-2" />
            ) : (
              <Download className="w-4 h-4 sm:mr-2" />
            )}
            <span className="hidden sm:inline">{downloadingPptx ? 'Generating...' : 'Download PPTX'}</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setCurrentSlide(0); setViewMode('presentation'); }} className="text-xs sm:text-sm px-2 sm:px-3 border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="btn-presentation">
            <Presentation className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Present as Slides</span>
          </Button>
          <Button variant="outline" size="sm" onClick={handlePrint} className="text-xs sm:text-sm px-2 sm:px-3" data-testid="btn-print-report">
            <Printer className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Print</span>
          </Button>
        </div>
      </header>

      {/* Report Content */}
      <main className="pt-16 sm:pt-20 pb-8 sm:pb-12 px-2 sm:px-4 md:px-6">
        <div className="report-container max-w-5xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
          
          {/* Cover / Header */}
          <div className="bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-800 text-white p-4 sm:p-8 md:p-12">
            <div className="flex items-center gap-2 mb-4 sm:mb-6">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg sm:text-xl">S</span>
              </div>
              <span className="text-lg sm:text-xl font-bold">SAMANVAI</span>
            </div>
            <h1 className="text-xl sm:text-3xl md:text-4xl font-bold mb-2 sm:mb-4" data-testid="report-title">{reportCompanyName} Value Scorecard</h1>
            <h2 className="text-base sm:text-xl md:text-2xl font-light mb-6 sm:mb-8 opacity-90">Comprehensive Performance Analysis & Strategic Roadmap</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6 pt-4 sm:pt-6 border-t border-white/20">
              <div><p className="text-xs sm:text-sm opacity-70">Company</p><p className="font-semibold text-sm sm:text-base">{reportCompanyName}</p></div>
              <div><p className="text-xs sm:text-sm opacity-70">Industry</p><p className="font-semibold text-sm sm:text-base">{report?.sector || analysis?.sector || 'N/A'}</p></div>
              <div><p className="text-xs sm:text-sm opacity-70">Prepared For</p><p className="font-semibold text-sm sm:text-base">{userName}</p></div>
              <div><p className="text-xs sm:text-sm opacity-70">Date</p><p className="font-semibold text-sm sm:text-base">{formatDate()}</p></div>
            </div>
          </div>

          {/* Executive Summary */}
          <CollapsibleSection title="Executive Summary" icon={BookOpen} iconBg="bg-emerald-100" iconColor="text-emerald-600">
            <div className="px-4 sm:px-8 md:px-12 pb-6 sm:pb-8">
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed whitespace-pre-line">{report?.executive_summary || analysis?.executive_summary}</p>
              {report?.projected_improvements && (
                <div className="mt-6 sm:mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-4">
                  {Object.entries(report.projected_improvements).filter(([, value]) => String(value).length <= 40).map(([key, value]) => (
                    <div key={key} className="bg-emerald-50 rounded-lg p-3 sm:p-4 text-center">
                      <p className="text-lg sm:text-2xl font-bold text-emerald-600">{value}</p>
                      <p className="text-xs text-gray-600 mt-1 capitalize">{key.replace(/_/g, ' ')}</p>
                    </div>
                  ))}
                </div>
              )}
              {report?.projected_improvements && Object.entries(report.projected_improvements).filter(([, value]) => String(value).length > 40).map(([key, value]) => (
                <p key={key} className="text-xs text-gray-500 mt-3" data-testid="projected-improvements-note">{value}</p>
              ))}
            </div>
          </CollapsibleSection>

          {/* Performance Overview */}
          <CollapsibleSection title="Performance Overview" icon={BarChart3} iconBg="bg-blue-100" iconColor="text-blue-600">
            <div className="px-4 sm:px-8 md:px-12 pb-6 sm:pb-8">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
                <div className="bg-emerald-50 rounded-xl p-4 text-center">
                  <TrendingUp className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                  <p data-testid="report-summary-above-average" className="text-2xl sm:text-3xl font-bold text-emerald-600">{kpisAboveAvg ?? 'N/A'}</p>
                  <p className="text-xs text-gray-600">KPIs Above Average</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-4 text-center">
                  <AlertTriangle className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                  <p data-testid="report-summary-below-average" className="text-2xl sm:text-3xl font-bold text-amber-600">{kpisBelowAvg ?? 'N/A'}</p>
                  <p className="text-xs text-gray-600">Need Attention</p>
                </div>
                <div className="bg-purple-50 rounded-xl p-4 text-center">
                  <Coins className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                  <p data-testid="report-summary-impact" className="text-2xl sm:text-3xl font-bold text-purple-600">{impactText(totalPotentialImpact, curSym, 0)}</p>
                  <p className="text-xs text-gray-600">1% Improvement Value</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <Target className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <p className="text-2xl sm:text-3xl font-bold text-blue-600">{kpiData.length}</p>
                  <p className="text-xs text-gray-600">KPIs Analyzed</p>
                </div>
              </div>
            </div>
          </CollapsibleSection>

          {/* KPI Analysis */}
          {kpiData.length > 0 && (
            <CollapsibleSection title="KPI Analysis" icon={Target} iconBg="bg-indigo-100" iconColor="text-indigo-600">
              <div className="px-4 sm:px-8 md:px-12 pb-6 sm:pb-8">
                <div className="flex items-center gap-4 text-xs mb-4 p-3 bg-gray-50 rounded-lg">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-500 rounded-full"></span><span className="text-red-600">Underperforming</span></span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded-full"></span><span className="text-emerald-600">Above Average</span></span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[600px]">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 sm:px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">KPI</th>
                        <th className="px-3 sm:px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Your Value</th>
                        <th className="px-3 sm:px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Industry Avg</th>
                        <th className="px-3 sm:px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Top Performer</th>
                        <th className="px-3 sm:px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">1% Impact</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {kpiData.map((kpi, idx) => {
                        const lowerIsBetter = isLowerBetter(kpi.name);
                        const comparison = metricComparison(kpi, lowerIsBetter);
                        const isAboveAvg = comparison === null || comparison >= 0;
                        return (
                          <tr key={idx} className={`${!isAboveAvg ? 'bg-red-50/50' : ''}`}>
                            <td className="px-3 sm:px-4 py-3"><div className="flex items-center gap-2">{!isAboveAvg && <span className="w-2 h-2 bg-red-500 rounded-full"></span>}<span className="font-medium text-gray-900 text-sm">{kpi.name}</span></div></td>
                            <td data-testid={`report-kpi-value-${idx}`} className={`px-3 sm:px-4 py-3 text-center font-semibold text-sm ${comparison === null ? 'text-gray-600' : !isAboveAvg ? 'text-red-600' : 'text-emerald-600'}`}>{metricText(kpi.yourValue, formatUnit(kpi.unit))}</td>
                            <td data-testid={`report-kpi-industry-${idx}`} className="px-3 sm:px-4 py-3 text-center text-gray-500 text-sm">{metricText(kpi.industryAvg, formatUnit(kpi.unit))}</td>
                            <td data-testid={`report-kpi-top-${idx}`} className="px-3 sm:px-4 py-3 text-center text-amber-600 text-sm">{metricText(kpi.topPerformer, formatUnit(kpi.unit))}</td>
                            <td data-testid={`report-kpi-impact-${idx}`} className="px-3 sm:px-4 py-3 text-right font-medium text-gray-900 text-sm">{impactText(kpi.impactPer1Percent, curSym)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </CollapsibleSection>
          )}

          {/* KPI Improvement Values */}
          {report?.kpi_improvement_values && report.kpi_improvement_values.length > 0 && (
            <CollapsibleSection title="KPI Improvement Values" icon={TrendingUp} iconBg="bg-cyan-100" iconColor="text-cyan-600">
              <div className="px-4 sm:px-8 md:px-12 pb-6 sm:pb-8">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[600px]">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase">KPI</th>
                        <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Current</th>
                        <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Target</th>
                        <th className="px-3 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Annual Impact</th>
                        <th className="px-3 py-3 text-right text-xs font-semibold text-emerald-600 uppercase">3-Year Value</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {report.kpi_improvement_values.map((kpi, idx) => (
                        <tr key={idx}>
                          <td className="px-3 py-3 font-medium text-gray-900 text-sm">{kpi.kpi_name}</td>
                          <td className="px-3 py-3 text-center text-gray-600 text-sm">{kpi.current_value}</td>
                          <td className="px-3 py-3 text-center text-blue-600 text-sm">{kpi.target_value}</td>
                          <td className="px-3 py-3 text-right font-medium text-gray-900 text-sm">{kpi.annual_value_impact}</td>
                          <td className="px-3 py-3 text-right font-bold text-emerald-600 text-sm">{kpi.three_year_value_impact}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </CollapsibleSection>
          )}

          {/* Peer Comparison */}
          {peers.length > 0 && peerComparison.length > 0 && (
            <CollapsibleSection title="Peer Comparison" icon={Building2} iconBg="bg-teal-100" iconColor="text-teal-600">
              <div className="px-4 sm:px-8 md:px-12 pb-6 sm:pb-8">
                <p className="text-sm text-gray-500 mb-4">Comparing <strong>{reportCompanyName}</strong> with: {peers.join(', ')}</p>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[500px]">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase">KPI</th>
                        <th className="px-3 py-3 text-center text-xs font-semibold text-emerald-600 uppercase bg-emerald-50">{reportCompanyName.slice(0, 15)}</th>
                        {peers[0] && <th className="px-3 py-3 text-center text-xs font-semibold text-blue-600 uppercase">{peers[0].slice(0, 15)}</th>}
                        {peers[1] && <th className="px-3 py-3 text-center text-xs font-semibold text-purple-600 uppercase">{peers[1].slice(0, 15)}</th>}
                        {peers[2] && <th className="px-3 py-3 text-center text-xs font-semibold text-orange-600 uppercase">{peers[2].slice(0, 15)}</th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {peerComparison.map((pc, idx) => (
                        <tr key={idx}>
                          <td className="px-3 py-3 font-medium text-gray-900 text-sm">{pc.name}</td>
                          <td data-testid={`report-peer-company-${idx}`} className="px-3 py-3 text-center font-semibold text-emerald-600 bg-emerald-50 text-sm">{metricText(pc.yourValue, formatUnit(pc.unit))}</td>
                          {peers[0] && <td className="px-3 py-3 text-center text-gray-600 text-sm">{pc.peer1Value ?? '-'}{pc.peer1Value != null ? formatUnit(pc.unit) : ''}</td>}
                          {peers[1] && <td className="px-3 py-3 text-center text-gray-600 text-sm">{pc.peer2Value ?? '-'}{pc.peer2Value != null ? formatUnit(pc.unit) : ''}</td>}
                          {peers[2] && <td className="px-3 py-3 text-center text-gray-600 text-sm">{pc.peer3Value ?? '-'}{pc.peer3Value != null ? formatUnit(pc.unit) : ''}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </CollapsibleSection>
          )}

          {/* Financial Impact Summary */}
          {report?.financial_impact_summary && (
            <CollapsibleSection title="Financial Impact Summary" icon={Banknote} iconBg="bg-gradient-to-br from-emerald-500 to-teal-600" iconColor="text-white">
              <div className="px-4 sm:px-8 md:px-12 pb-6 sm:pb-8 bg-gradient-to-br from-emerald-50 to-teal-50">
                <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-8 shadow-lg border border-emerald-100 mb-4 sm:mb-8">
                  <div className="text-center">
                    <p className="text-xs sm:text-sm font-medium text-gray-500 mb-2">TOTAL BUSINESS VALUE POTENTIAL</p>
                    <p className="text-3xl sm:text-5xl md:text-6xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">{report.financial_impact_summary.total_business_value}</p>
                    <p className="text-xs sm:text-sm text-gray-500 mt-2">Over 3-Year Implementation Period</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
                  <div className="bg-white rounded-xl p-3 sm:p-6 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2"><div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-lg flex items-center justify-center"><TrendingDown className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" /></div><span className="text-xs sm:text-sm font-medium text-gray-500">Cost Savings</span></div>
                    <p className="text-lg sm:text-2xl font-bold text-gray-900">{report.financial_impact_summary.total_potential_savings}</p>
                  </div>
                  <div className="bg-white rounded-xl p-3 sm:p-6 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2"><div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center"><TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" /></div><span className="text-xs sm:text-sm font-medium text-gray-500">Revenue Uplift</span></div>
                    <p className="text-lg sm:text-2xl font-bold text-gray-900">{report.financial_impact_summary.revenue_uplift_potential}</p>
                  </div>
                  <div className="bg-white rounded-xl p-3 sm:p-6 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2"><div className="w-8 h-8 sm:w-10 sm:h-10 bg-amber-100 rounded-lg flex items-center justify-center"><Zap className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" /></div><span className="text-xs sm:text-sm font-medium text-gray-500">Efficiency</span></div>
                    <p className="text-lg sm:text-2xl font-bold text-gray-900">{report.financial_impact_summary.efficiency_gains_value}</p>
                  </div>
                  {report.financial_impact_summary.cost_reduction_potential && (
                    <div className="bg-white rounded-xl p-3 sm:p-6 shadow-sm border border-gray-100">
                      <div className="flex items-center gap-2 sm:gap-3 mb-2"><div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-lg flex items-center justify-center"><Settings className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" /></div><span className="text-xs sm:text-sm font-medium text-gray-500">Cost Reduction</span></div>
                      <p className="text-lg sm:text-2xl font-bold text-gray-900">{report.financial_impact_summary.cost_reduction_potential}</p>
                    </div>
                  )}
                  {report.financial_impact_summary.competitive_advantage_value && (
                    <div className="bg-white rounded-xl p-3 sm:p-6 shadow-sm border border-gray-100 col-span-2">
                      <div className="flex items-center gap-2 sm:gap-3 mb-2"><div className="w-8 h-8 sm:w-10 sm:h-10 bg-indigo-100 rounded-lg flex items-center justify-center"><Award className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" /></div><span className="text-xs sm:text-sm font-medium text-gray-500">Competitive Advantage</span></div>
                      <p className="text-lg sm:text-2xl font-bold text-gray-900">{report.financial_impact_summary.competitive_advantage_value}</p>
                    </div>
                  )}
                </div>
                {(report.financial_impact_summary.reconciliation_note || report.financial_impact_summary.mece_note) && (
                  <div className="mt-4 bg-white/70 rounded-lg p-3 border border-emerald-100" data-testid="fis-governance-notes">
                    {report.financial_impact_summary.reconciliation_note && (
                      <p className="text-xs text-gray-600"><span className="font-semibold text-gray-800">Reconciliation:</span> {report.financial_impact_summary.reconciliation_note}</p>
                    )}
                    {report.financial_impact_summary.mece_note && (
                      <p className="text-xs text-gray-500 mt-1">{report.financial_impact_summary.mece_note}</p>
                    )}
                  </div>
                )}
              </div>
            </CollapsibleSection>
          )}

          {/* ROI Analysis */}
          {report?.roi_analysis && (
            <CollapsibleSection title="ROI Analysis" icon={PieChart} iconBg="bg-green-100" iconColor="text-green-600">
              <div className="px-4 sm:px-8 md:px-12 pb-6 sm:pb-8">
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 text-center border border-green-100">
                    <p className="text-sm text-gray-500">Year 1 ROI</p>
                    <p className="text-2xl sm:text-3xl font-bold text-green-600">{report.roi_analysis.year_1_roi}</p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 text-center border border-blue-100">
                    <p className="text-sm text-gray-500">Year 3 ROI</p>
                    <p className="text-2xl sm:text-3xl font-bold text-blue-600">{report.roi_analysis.year_3_roi}</p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-4 text-center border border-purple-100">
                    <p className="text-sm text-gray-500">Payback Period</p>
                    <p className="text-2xl sm:text-3xl font-bold text-purple-600">{report.roi_analysis.payback_period}</p>
                  </div>
                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 text-center border border-amber-100">
                    <p className="text-sm text-gray-500">NPV</p>
                    <p className="text-2xl sm:text-3xl font-bold text-amber-600">{report.roi_analysis.net_present_value}</p>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 sm:p-6">
                  <h4 className="font-semibold text-gray-900 mb-4">Annual Financial Projections</h4>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div><p className="text-xs text-gray-500 mb-1">Year 1</p><p className="font-bold text-lg sm:text-xl text-gray-900">{report.roi_analysis.year_1_value}</p></div>
                    <div><p className="text-xs text-gray-500 mb-1">Year 2</p><p className="font-bold text-lg sm:text-xl text-gray-900">{report.roi_analysis.year_2_value}</p></div>
                    <div><p className="text-xs text-gray-500 mb-1">Year 3</p><p className="font-bold text-lg sm:text-xl text-gray-900">{report.roi_analysis.year_3_value}</p></div>
                  </div>
                </div>
                {(report.roi_analysis.assumptions || report.roi_analysis.reconciliation || report?.scenario_analysis || report?.confidence_statement) && (
                  <div className="mt-4 bg-slate-50 rounded-xl p-4 sm:p-6 border border-slate-200" data-testid="roi-assumptions-block">
                    <h4 className="font-semibold text-gray-900 mb-3">Model Assumptions &amp; Reconciliation</h4>
                    {report.roi_analysis.assumptions && (
                      <ul className="space-y-1 mb-3">
                        {Object.entries(report.roi_analysis.assumptions).slice(0, 8).map(([k, v]) => (
                          <li key={k} className="text-xs sm:text-sm text-gray-600"><span className="font-medium text-gray-800 capitalize">{k.replace(/_/g, ' ')}:</span> {String(v)}</li>
                        ))}
                      </ul>
                    )}
                    {report.roi_analysis.reconciliation && (
                      <p className="text-xs sm:text-sm text-gray-600 mb-3"><span className="font-medium text-gray-800">Reconciliation:</span> {report.roi_analysis.reconciliation}</p>
                    )}
                    {report?.scenario_analysis && (
                      <div className="grid grid-cols-3 gap-3 mb-3" data-testid="scenario-analysis-grid">
                        <div className="bg-white rounded-lg p-3 text-center border border-slate-200"><p className="text-[11px] text-gray-500">Conservative</p><p className="font-bold text-gray-800">{report.scenario_analysis.conservative}</p></div>
                        <div className="bg-white rounded-lg p-3 text-center border border-emerald-200"><p className="text-[11px] text-gray-500">Base</p><p className="font-bold text-emerald-700">{report.scenario_analysis.base}</p></div>
                        <div className="bg-white rounded-lg p-3 text-center border border-slate-200"><p className="text-[11px] text-gray-500">Stretch</p><p className="font-bold text-gray-800">{report.scenario_analysis.stretch}</p></div>
                      </div>
                    )}
                    {report?.scenario_analysis?.basis && (
                      <p className="text-xs text-gray-500 mb-2 italic">{report.scenario_analysis.basis}</p>
                    )}
                    {report?.confidence_statement && (
                      <p className="text-xs text-gray-500 border-t border-slate-200 pt-3" data-testid="confidence-statement">{report.confidence_statement}</p>
                    )}
                  </div>
                )}
              </div>
            </CollapsibleSection>
          )}

          {/* Business Value Realization */}
          {report?.business_value_realization && (
            <CollapsibleSection title="Business Value Realization Timeline" icon={Clock} iconBg="bg-cyan-100" iconColor="text-cyan-600">
              <div className="px-4 sm:px-8 md:px-12 pb-6 sm:pb-8">
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl p-4 sm:p-6 border border-yellow-200">
                    <div className="flex items-center gap-2 mb-3"><Zap className="w-6 h-6 text-yellow-500" /><h4 className="font-bold text-gray-900">Quick Wins</h4></div>
                    <p className="text-xs text-gray-500 mb-2">0-6 months</p>
                    <p className="text-sm text-gray-600 mb-3">{report.business_value_realization.quick_wins_description}</p>
                    <p className="text-xl font-bold text-yellow-600">{report.business_value_realization.quick_wins_value}</p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 sm:p-6 border border-blue-200">
                    <div className="flex items-center gap-2 mb-3"><TrendingUp className="w-6 h-6 text-blue-500" /><h4 className="font-bold text-gray-900">Medium Term</h4></div>
                    <p className="text-xs text-gray-500 mb-2">6-18 months</p>
                    <p className="text-sm text-gray-600 mb-3">{report.business_value_realization.medium_term_description}</p>
                    <p className="text-xl font-bold text-blue-600">{report.business_value_realization.medium_term_value}</p>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 sm:p-6 border border-green-200">
                    <div className="flex items-center gap-2 mb-3"><Target className="w-6 h-6 text-green-500" /><h4 className="font-bold text-gray-900">Long Term</h4></div>
                    <p className="text-xs text-gray-500 mb-2">18-36 months</p>
                    <p className="text-sm text-gray-600 mb-3">{report.business_value_realization.long_term_description}</p>
                    <p className="text-xl font-bold text-green-600">{report.business_value_realization.long_term_value}</p>
                  </div>
                </div>
              </div>
            </CollapsibleSection>
          )}

          {/* 5-Year Roadmap */}
          {fiveYearRoadmap.length > 0 && (
            <CollapsibleSection title="5-Year Strategic Roadmap" icon={Calendar} iconBg="bg-purple-100" iconColor="text-purple-600">
              <div className="px-4 sm:px-8 md:px-12 pb-6 sm:pb-8">
                <div className="space-y-4">
                  {fiveYearRoadmap.map((year, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3"><span className="text-xl font-bold">Year {idx + 1}</span><span className="text-white/80">|</span><span className="font-medium">{year.focus}</span></div>
                          {year.estimated_investment && <span className="bg-white/20 px-3 py-1 rounded-full text-sm">{year.estimated_investment}</span>}
                        </div>
                      </div>
                      <div className="p-4 grid sm:grid-cols-2 gap-4">
                        {year.goals && year.goals.length > 0 && (
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2 text-sm"><Target className="w-4 h-4 text-emerald-600" />Goals</h4>
                            <ul className="space-y-1">{year.goals.map((goal, gIdx) => (<li key={gIdx} className="flex items-start gap-2 text-xs text-gray-600"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />{goal}</li>))}</ul>
                          </div>
                        )}
                        {year.technology_required && year.technology_required.length > 0 && (
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2 text-sm"><Cpu className="w-4 h-4 text-blue-600" />Technology</h4>
                            <div className="flex flex-wrap gap-1">{year.technology_required.map((tech, tIdx) => (<span key={tIdx} className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">{tech}</span>))}</div>
                          </div>
                        )}
                        {year.how_to_achieve && year.how_to_achieve.length > 0 && (
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2 text-sm"><Wrench className="w-4 h-4 text-amber-600" />How to Achieve</h4>
                            <ul className="space-y-1">{year.how_to_achieve.slice(0, 3).map((step, sIdx) => (<li key={sIdx} className="flex items-start gap-2 text-xs text-gray-600"><ArrowRight className="w-3 h-3 text-amber-500 mt-0.5 flex-shrink-0" />{step}</li>))}</ul>
                          </div>
                        )}
                        {year.manpower_required && (
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2 text-sm"><UserCheck className="w-4 h-4 text-indigo-600" />Manpower</h4>
                            <p className="text-xs text-gray-600 bg-indigo-50 rounded p-2">{year.manpower_required}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CollapsibleSection>
          )}

          {/* Value Creation Framework (EOAI) */}
          <section className="p-4 sm:p-8 md:p-12 border-b border-gray-100 bg-gray-50">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 text-center">Value Creation Framework</h2>
            <p className="text-sm text-gray-500 text-center mb-6">Eliminate • Optimize • Automate • Industrialize (EOAI)</p>
            <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
              <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-red-100">
                <div className="flex items-center gap-3 mb-4"><div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center"><XCircle className="w-5 h-5 text-red-600" /></div><h3 className="text-lg sm:text-xl font-bold text-gray-900">Eliminate</h3></div>
                <p className="text-xs sm:text-sm text-gray-500 mb-4">Remove inefficiencies and non-value activities</p>
                <div className="space-y-3">{report?.eliminate?.map((item, idx) => (<div key={idx} className="border-l-4 border-red-400 pl-3 py-2"><h4 className="font-semibold text-gray-900 text-sm">{item.title}</h4><p className="text-xs text-gray-600 mt-1">{item.description}</p><div className="flex flex-wrap gap-2 mt-2">{item.impact && <span className="text-xs bg-red-50 text-red-700 px-2 py-0.5 rounded">{item.impact}</span>}{item.estimated_savings && <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded font-medium">{item.estimated_savings}</span>}</div></div>))}</div>
              </div>
              <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-amber-100">
                <div className="flex items-center gap-3 mb-4"><div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center"><Settings className="w-5 h-5 text-amber-600" /></div><h3 className="text-lg sm:text-xl font-bold text-gray-900">Optimize</h3></div>
                <p className="text-xs sm:text-sm text-gray-500 mb-4">Improve processes for maximum efficiency</p>
                <div className="space-y-3">{report?.optimize?.map((item, idx) => (<div key={idx} className="border-l-4 border-amber-400 pl-3 py-2"><h4 className="font-semibold text-gray-900 text-sm">{item.title}</h4><p className="text-xs text-gray-600 mt-1">{item.description}</p><div className="flex flex-wrap gap-2 mt-2">{item.impact && <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded">{item.impact}</span>}{item.estimated_savings && <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded font-medium">{item.estimated_savings}</span>}</div></div>))}</div>
              </div>
              <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-blue-100">
                <div className="flex items-center gap-3 mb-4"><div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center"><Bot className="w-5 h-5 text-blue-600" /></div><h3 className="text-lg sm:text-xl font-bold text-gray-900">Automate</h3></div>
                <p className="text-xs sm:text-sm text-gray-500 mb-4">Leverage technology to reduce manual effort</p>
                <div className="space-y-3">{report?.automate?.map((item, idx) => (<div key={idx} className="border-l-4 border-blue-400 pl-3 py-2"><h4 className="font-semibold text-gray-900 text-sm">{item.title}</h4><p className="text-xs text-gray-600 mt-1">{item.description}</p><div className="flex flex-wrap gap-2 mt-2">{item.impact && <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{item.impact}</span>}{item.estimated_savings && <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded font-medium">{item.estimated_savings}</span>}</div></div>))}</div>
              </div>
              <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-purple-100">
                <div className="flex items-center gap-3 mb-4"><div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center"><Factory className="w-5 h-5 text-purple-600" /></div><h3 className="text-lg sm:text-xl font-bold text-gray-900">Industrialize</h3></div>
                <p className="text-xs sm:text-sm text-gray-500 mb-4">Scale and standardize for enterprise-wide impact</p>
                <div className="space-y-3">{report?.industrialize?.map((item, idx) => (<div key={idx} className="border-l-4 border-purple-400 pl-3 py-2"><h4 className="font-semibold text-gray-900 text-sm">{item.title}</h4><p className="text-xs text-gray-600 mt-1">{item.description}</p><div className="flex flex-wrap gap-2 mt-2">{item.impact && <span className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded">{item.impact}</span>}{item.estimated_savings && <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded font-medium">{item.estimated_savings}</span>}</div></div>))}</div>
              </div>
            </div>
          </section>

          {/* Investment & Resources */}
          {(report?.total_investment || report?.investment_breakdown?.length > 0) && (
            <CollapsibleSection title="Investment & Resources" icon={DollarSign} iconBg="bg-indigo-100" iconColor="text-indigo-600">
              <div className="px-4 sm:px-8 md:px-12 pb-6 sm:pb-8">
                {report?.total_investment && (
                  <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl p-4 sm:p-6 mb-6 text-center">
                    <p className="text-sm opacity-80 mb-1">TOTAL INVESTMENT REQUIRED</p>
                    <p className="text-3xl sm:text-4xl font-bold">{report.total_investment}</p>
                  </div>
                )}
                {report?.investment_breakdown && report.investment_breakdown.length > 0 && (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {report.investment_breakdown.map((item, idx) => (
                      <div key={idx} className="bg-gray-50 rounded-xl p-4">
                        <h4 className="font-bold text-gray-900 text-sm mb-1">{item.area}</h4>
                        <p className="text-lg font-bold text-indigo-600">{item.amount}</p>
                        <p className="text-xs text-gray-500">{item.percentage} of total</p>
                        {item.description && <p className="text-xs text-gray-600 mt-2">{item.description}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CollapsibleSection>
          )}

          {/* Implementation Plan */}
          {report?.implementation_plan && report.implementation_plan.length > 0 && (
            <CollapsibleSection title="Implementation Plan" icon={ListOrdered} iconBg="bg-orange-100" iconColor="text-orange-600">
              <div className="px-4 sm:px-8 md:px-12 pb-6 sm:pb-8">
                <div className="space-y-3">
                  {report.implementation_plan.map((step, idx) => (
                    <div key={idx} className="flex items-start gap-4 bg-gray-50 rounded-xl p-4">
                      <div className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">{idx + 1}</div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-bold text-gray-900 text-sm">{step.step}</h4>
                          <span className="text-xs text-orange-600 bg-orange-100 px-2 py-0.5 rounded">{step.timeline}</span>
                        </div>
                        <p className="text-xs text-gray-600">{step.description}</p>
                        {step.resources && <p className="text-xs text-blue-600 mt-1">Resources: {step.resources}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CollapsibleSection>
          )}

          {/* Key Recommendations */}
          {areasOfImprovement.length > 0 && (
            <CollapsibleSection title="Key Recommendations" icon={Lightbulb} iconBg="bg-yellow-100" iconColor="text-yellow-600">
              <div className="px-4 sm:px-8 md:px-12 pb-6 sm:pb-8">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {areasOfImprovement.map((area, idx) => (
                    <div key={idx} className={`rounded-xl p-4 ${area.priority === 'High' ? 'bg-red-50 border-l-4 border-red-500' : area.priority === 'Medium' ? 'bg-amber-50 border-l-4 border-amber-500' : 'bg-gray-50 border-l-4 border-gray-400'}`}>
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-bold text-gray-900 text-sm">{area.area}</h4>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${area.priority === 'High' ? 'bg-red-100 text-red-700' : area.priority === 'Medium' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'}`}>{area.priority}</span>
                      </div>
                      <p className="text-xs text-emerald-600 font-medium">{area.potentialImpact}</p>
                      {area.timeline && <p className="text-xs text-gray-500 mt-1">{area.timeline}</p>}
                    </div>
                  ))}
                </div>
              </div>
            </CollapsibleSection>
          )}

          {/* Case Studies */}
          {report?.case_studies && report.case_studies.length > 0 && (
            <CollapsibleSection title="Industry Case Studies" icon={Building2} iconBg="bg-orange-100" iconColor="text-orange-600">
              <div className="px-4 sm:px-8 md:px-12 pb-6 sm:pb-8">
                <div className="space-y-4">
                  {report.case_studies.map((cs, idx) => (
                    <div key={idx} className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-4 sm:p-6 border border-orange-100">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center text-white font-bold text-lg">{cs.company?.charAt(0) || 'C'}</div>
                        <div><h4 className="font-bold text-gray-900">{cs.company}</h4><p className="text-sm text-orange-600">{cs.industry}</p></div>
                      </div>
                      <div className="space-y-3 text-sm">
                        <div><p className="font-semibold text-red-600 mb-1">Challenge</p><p className="text-gray-600">{cs.challenge}</p></div>
                        <div><p className="font-semibold text-blue-600 mb-1">Solution</p><p className="text-gray-600">{cs.solution}</p></div>
                        <div><p className="font-semibold text-emerald-600 mb-1">Results</p><p className="text-gray-700 font-medium">{cs.results}</p></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CollapsibleSection>
          )}

          {/* References & Sources */}
          {report?.references && report.references.length > 0 && (
            <CollapsibleSection title="References & Sources" icon={BookOpen} iconBg="bg-slate-100" iconColor="text-slate-700">
              <div className="px-4 sm:px-8 md:px-12 pb-6 sm:pb-8">
                <p className="text-xs text-gray-500 mb-4">
                  The analysis above draws on the following public disclosures, industry benchmarks, market research and methodologies. Every figure in your Value Scorecard can be traced back to one or more of these sources.
                </p>
                {(() => {
                  const groups = {};
                  report.references.forEach((r) => {
                    const k = r.category || 'Other';
                    if (!groups[k]) groups[k] = [];
                    groups[k].push(r);
                  });
                  const order = ['Public Disclosure', 'Industry Benchmark', 'Market Research', 'News / Analyst', 'Regulatory / Standards', 'Methodology', 'Other'];
                  const cats = Object.keys(groups).sort((a, b) => {
                    const ia = order.indexOf(a); const ib = order.indexOf(b);
                    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
                  });
                  const ACCENT = {
                    'Public Disclosure':       { dot: 'bg-emerald-500', label: 'text-emerald-700', bg: 'bg-emerald-50',  border: 'border-emerald-200' },
                    'Industry Benchmark':      { dot: 'bg-blue-500',    label: 'text-blue-700',    bg: 'bg-blue-50',     border: 'border-blue-200'    },
                    'Market Research':         { dot: 'bg-violet-500',  label: 'text-violet-700',  bg: 'bg-violet-50',   border: 'border-violet-200'  },
                    'News / Analyst':          { dot: 'bg-rose-500',    label: 'text-rose-700',    bg: 'bg-rose-50',     border: 'border-rose-200'    },
                    'Regulatory / Standards':  { dot: 'bg-amber-500',   label: 'text-amber-700',   bg: 'bg-amber-50',    border: 'border-amber-200'   },
                    'Methodology':             { dot: 'bg-slate-500',   label: 'text-slate-700',   bg: 'bg-slate-50',    border: 'border-slate-200'   },
                    'Other':                   { dot: 'bg-gray-400',    label: 'text-gray-700',    bg: 'bg-gray-50',     border: 'border-gray-200'    },
                  };
                  return (
                    <div className="space-y-6" data-testid="references-section">
                      {cats.map((cat) => {
                        const c = ACCENT[cat] || ACCENT['Other'];
                        return (
                          <div key={cat}>
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
                              <h4 className={`text-[10px] tracking-[0.25em] font-bold uppercase ${c.label}`}>{cat}</h4>
                            </div>
                            <ul className="space-y-1.5">
                              {groups[cat].map((r, i) => (
                                <li key={i} className={`text-xs text-gray-700 leading-relaxed pl-3 border-l-2 ${c.border} ${c.bg} px-3 py-1.5 rounded-r-md`}>
                                  <span className="font-semibold text-gray-900">{r.title}</span>
                                  {r.publisher ? <span className="text-gray-500"> · {r.publisher}</span> : null}
                                  {r.year ? <span className="text-gray-500"> · {r.year}</span> : null}
                                  {r.url ? (
                                    <a href={r.url} target="_blank" rel="noopener noreferrer" className="ml-1 text-blue-600 hover:underline">↗</a>
                                  ) : null}
                                  {r.note ? <div className="text-[11px] text-gray-500 italic mt-0.5">{r.note}</div> : null}
                                </li>
                              ))}
                            </ul>
                          </div>
                        );
                      })}
                      <div className="text-[10px] text-gray-400 italic pt-2 border-t border-gray-200">
                        Citations are provided for transparency. Where canonical URLs are unavailable, refer to the publisher&apos;s primary website or contact SAMANVAI for the underlying dataset reference.
                      </div>
                    </div>
                  );
                })()}
              </div>
            </CollapsibleSection>
          )}

          <ProposalGenerator assessmentId={analysis?.id} userId={analysis?.user_id}
            companyName={analysis?.company_name || companyName} />

          <div className="px-4 sm:px-8 pb-6"><ReportSecurityNote /></div>

          {/* Footer */}
          <div className="bg-gray-900 text-white p-4 sm:p-8 text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center"><span className="text-white font-bold">S</span></div>
              <span className="text-lg font-bold">SAMANVAI</span>
            </div>
            <p className="text-gray-400 text-sm mb-2">AI-Powered Executive Intelligence Platform</p>
            <p className="text-gray-500 text-xs">This report was generated using advanced AI analysis. Data accuracy depends on input quality.</p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ReportPage;
